/**
 * Module-level Logto token provider.
 *
 * LogtoStreamingProvider sets the provider function on mount;
 * non-React code (e.g. adminRequest) can call getLogtoToken()
 * to obtain the current access token without React hooks.
 *
 * Uses a generation counter so that when route changes cause
 * old provider cleanup to run AFTER the new provider has been set,
 * the stale clear does not wipe the fresh provider.
 */

type TokenProvider = (resource?: string) => Promise<string | undefined>;

let _provider: TokenProvider | null = null;
let _generation = 0;

/**
 * Set the token provider. Returns the generation id for use in clearLogtoTokenProvider.
 */
export function setLogtoTokenProvider(fn: TokenProvider): number {
  _generation++;
  _provider = fn;
  return _generation;
}

/**
 * Clear the token provider only if the generation matches.
 * This prevents a stale cleanup from wiping a freshly-set provider.
 */
export function clearLogtoTokenProvider(generation: number): void {
  if (generation === _generation) {
    _provider = null;
  }
}

export async function getLogtoToken(
  resource?: string,
): Promise<string | undefined> {
  return _provider?.(resource);
}
